/**
 * /ai/decision-registry — Session 3a PR 17.
 *
 * Admin browser for the M3X.1 v2 Decision Registry — every LLM
 * call-site is a registered Decision per commandment 43; this surface
 * lets HR see what's registered. Solvait infrastructure-owned per
 * Session 3a §4.16.
 *
 * AI surfaces (commandment-3 invariant):
 *   1. AiBriefPanel right rail (infrastructure narration via Mutaba'aa
 *      brand attribution)
 *   2. InlineAISuggestion cards (dead-code candidate · peak usage)
 *   3. TraceableValue wrapping confidence_threshold (≥3 wrappers via
 *      per-row confidence + last_used + invocation_count)
 */

import { useQuery } from "@tanstack/react-query";
import { Download, ScanLine, Search } from "lucide-react";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

import { ErrorPanel, Loading } from "../components/Loading";
import { AgentBadge } from "../components/ui/AgentBadge";
import {
  InlineAISuggestion,
  PageHeader,
  TraceableValue,
} from "../components/ui";
import {
  session3aApi,
  type DecisionRegistryItem,
} from "../lib/session3a-api";
import { AiBriefPanel } from "../shell/AiBriefPanel";
import { severityChip, showAlertTrace } from "./_session3aHelpers";

type AgentFilter = "all" | "tashghil" | "rawatib" | "mutabaa" | "khidmat";

export function AIDecisionRegistryPage() {
  const { t } = useTranslation();
  const [agentFilter, setAgentFilter] = useState<AgentFilter>("all");
  const [modeFilter, setModeFilter] = useState<
    "all" | "ALWAYS_HUMAN_FIRST" | "ALWAYS_AUTONOMOUS" | "DEFAULT"
  >("all");
  const [usageFilter, setUsageFilter] = useState<"all" | "recent" | "never">("all");

  const list = useQuery({
    queryKey: ["decision-registry", agentFilter, modeFilter],
    queryFn: () =>
      session3aApi.listDecisionRegistry({
        owning_agent: agentFilter === "all" ? undefined : agentFilter,
        mode_shape: modeFilter === "all" ? undefined : modeFilter,
      }),
  });

  const items: DecisionRegistryItem[] = list.data?.items ?? [];

  const stats = useMemo(() => {
    const humanFirst = items.filter((r) => r.mode_shape === "ALWAYS_HUMAN_FIRST").length;
    const neverUsed = items.filter((r) => !r.last_used_at).length;
    const recentlyUsed = items.filter(
      (r) =>
        r.last_used_at &&
        Date.now() - new Date(r.last_used_at).getTime() < 24 * 60 * 60 * 1000,
    ).length;
    const modules = new Set(items.map((r) => r.source_module));
    return {
      total: items.length,
      humanFirst,
      neverUsed,
      recentlyUsed,
      moduleCount: modules.size,
      neverUsedDecision: items.find((r) => !r.last_used_at),
      peakUsage: items.reduce<DecisionRegistryItem | null>(
        (acc, r) => (!acc || r.invocation_count > acc.invocation_count ? r : acc),
        null,
      ),
    };
  }, [items]);

  const filtered = useMemo(() => {
    if (usageFilter === "recent")
      return items.filter(
        (r) =>
          r.last_used_at &&
          Date.now() - new Date(r.last_used_at).getTime() < 24 * 60 * 60 * 1000,
      );
    if (usageFilter === "never") return items.filter((r) => !r.last_used_at);
    return items;
  }, [items, usageFilter]);

  return (
    <div className="grid grid-cols-[1fr_280px] gap-4">
      <div className="space-y-3">
        <PageHeader
          title={t("ai.decision_registry.title", "AI Decision Registry")}
          subtitle={t(
            "ai.decision_registry.subtitle",
            "Every LLM call-site is a registered Decision per commandment 43. This is the inspector for the in-memory DecisionRegistry singleton.",
          )}
        />

        <div className="flex flex-wrap items-center justify-between gap-2">
          <div className="flex flex-wrap gap-1.5">
            {(
              [
                ["all", "All agents"],
                ["tashghil", "Tashghil"],
                ["rawatib", "Rawatib"],
                ["mutabaa", "Mutaba'aa"],
                ["khidmat", "Khidmat"],
              ] as const
            ).map(([key, label]) => (
              <button
                key={key}
                type="button"
                onClick={() => setAgentFilter(key)}
                className={`rounded-full border px-3 py-1 text-xs font-medium ${
                  agentFilter === key
                    ? "border-brand-blue bg-brand-blue/10 text-brand-deep"
                    : "border-border-subtle text-text-secondary hover:bg-surface-sunken"
                }`}
              >
                {t(`ai.decision_registry.filter.${key}`, label)}
              </button>
            ))}
            <button
              type="button"
              onClick={() =>
                setModeFilter(
                  modeFilter === "ALWAYS_HUMAN_FIRST" ? "all" : "ALWAYS_HUMAN_FIRST",
                )
              }
              className={`rounded-full border px-3 py-1 text-xs font-medium ${
                modeFilter === "ALWAYS_HUMAN_FIRST"
                  ? "border-purple-300 bg-purple-50 text-purple-800"
                  : "border-border-subtle text-text-secondary hover:bg-surface-sunken"
              }`}
            >
              {t(
                "ai.decision_registry.filter.human_first",
                "ALWAYS_HUMAN_FIRST",
              )}
            </button>
            <button
              type="button"
              onClick={() =>
                setUsageFilter(usageFilter === "recent" ? "all" : "recent")
              }
              className={`rounded-full border px-3 py-1 text-xs font-medium ${
                usageFilter === "recent"
                  ? "border-emerald-300 bg-emerald-50 text-emerald-800"
                  : "border-border-subtle text-text-secondary hover:bg-surface-sunken"
              }`}
            >
              {t("ai.decision_registry.filter.recent", "Recently used")}
            </button>
            <button
              type="button"
              onClick={() =>
                setUsageFilter(usageFilter === "never" ? "all" : "never")
              }
              className={`rounded-full border px-3 py-1 text-xs font-medium ${
                usageFilter === "never"
                  ? "border-amber-300 bg-amber-50 text-amber-800"
                  : "border-border-subtle text-text-secondary hover:bg-surface-sunken"
              }`}
            >
              {t("ai.decision_registry.filter.never", "Never used")}
            </button>
          </div>
          <button
            type="button"
            onClick={() =>
              showAlertTrace(
                t(
                  "ai.decision_registry.export_note",
                  "Snapshot exports the in-memory DecisionRegistry contents at this instant. Wires to GET /api/ai/decisions/snapshot.",
                ),
              )
            }
            className="inline-flex items-center gap-1 rounded-md border border-border-subtle px-2 py-1 text-xs text-text-secondary hover:bg-surface-sunken"
          >
            <Download className="h-3 w-3" />
            {t("ai.decision_registry.cta.export", "Export snapshot")}
          </button>
        </div>

        {stats.neverUsedDecision && (
          <InlineAISuggestion
            agent="mutabaa"
            icon={<ScanLine className="h-4 w-4" />}
            severity="info"
            headline={t(
              "ai.decision_registry.suggestion.dead_headline",
              `${stats.neverUsed} decisions registered but never invoked — possible dead code.`,
            )}
            body={t(
              "ai.decision_registry.suggestion.dead_body",
              "Consider auditing — either remove the unused registration or wire the missing call-site.",
            )}
            primaryAction={{
              label: t(
                "ai.decision_registry.suggestion.show_unused",
                "Show unused",
              ),
              onClick: () => setUsageFilter("never"),
            }}
            suggestionKey={`registry-dead-${stats.neverUsed}`}
          />
        )}
        {stats.peakUsage && stats.peakUsage.invocation_count > 0 && (
          <InlineAISuggestion
            agent="rawatib"
            icon={<Search className="h-4 w-4" />}
            severity="info"
            headline={t(
              "ai.decision_registry.suggestion.peak_headline",
              `Peak usage: ${stats.peakUsage.decision_id} (${stats.peakUsage.invocation_count} invocations).`,
            )}
            body={t(
              "ai.decision_registry.suggestion.peak_body",
              "High-volume decisions are good caching candidates. Review for memoization opportunities.",
            )}
            primaryAction={{
              label: t(
                "ai.decision_registry.suggestion.open_reasoning",
                "Open reasoning traces",
              ),
              onClick: () =>
                window.location.assign(
                  `/ai/reasoning?decision_id=${stats.peakUsage!.decision_id}`,
                ),
            }}
            suggestionKey={`registry-peak-${stats.peakUsage.decision_id}`}
          />
        )}

        {list.isLoading && <Loading />}
        {list.isError && <ErrorPanel message={(list.error as Error).message} />}

        {filtered.length > 0 && (
          <div className="overflow-hidden rounded-2xl border border-border-subtle bg-white">
            <table className="w-full text-sm">
              <thead className="bg-surface-sunken">
                <tr className="text-left text-[10px] font-semibold uppercase tracking-wide text-text-secondary">
                  <th className="px-3 py-2">{t("ai.decision_registry.col.decision_id", "Decision ID")}</th>
                  <th className="px-3 py-2">{t("ai.decision_registry.col.owning_agent", "Owning agent")}</th>
                  <th className="px-3 py-2">{t("ai.decision_registry.col.type", "Type")}</th>
                  <th className="px-3 py-2">{t("ai.decision_registry.col.context_bundle", "Context bundle")}</th>
                  <th className="px-3 py-2">{t("ai.decision_registry.col.mode_shape", "Mode shape")}</th>
                  <th className="px-3 py-2">{t("ai.decision_registry.col.thresholds", "Confidence")}</th>
                  <th className="px-3 py-2">{t("ai.decision_registry.col.last_used", "Last used")}</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((row) => (
                  <tr
                    key={row.decision_id}
                    className="border-t border-border-subtle hover:bg-surface-sunken/40"
                  >
                    <td className="px-3 py-2">
                      <button
                        type="button"
                        onClick={() =>
                          showAlertTrace(
                            t(
                              "ai.decision_registry.detail_note",
                              `Decision ${row.decision_id} — full registration site: ${row.source_module}. Drawer surfaces invocation log when wiring lands.`,
                            ),
                          )
                        }
                        className="font-mono text-xs text-[#2260C9] hover:underline"
                      >
                        {row.decision_id}
                      </button>
                    </td>
                    <td className="px-3 py-2">
                      <AgentBadge agent={row.owning_agent} variant="compact" />
                    </td>
                    <td className="px-3 py-2">
                      <span
                        className={`inline-flex items-center rounded-full px-2 py-0.5 text-[10px] font-medium ${severityChip(
                          row.decision_type === "DET"
                            ? "neutral"
                            : row.decision_type === "AI"
                              ? "info"
                              : "attention",
                        )}`}
                      >
                        {row.decision_type}
                      </span>
                    </td>
                    <td className="px-3 py-2 font-mono text-xs text-text-tertiary">
                      {row.context_bundle_class.split(".").pop()}
                    </td>
                    <td className="px-3 py-2">
                      <span
                        className={`inline-flex items-center rounded-full px-2 py-0.5 text-[10px] font-medium ${severityChip(
                          row.mode_shape === "ALWAYS_HUMAN_FIRST"
                            ? "veto"
                            : row.mode_shape === "ALWAYS_AUTONOMOUS"
                              ? "info"
                              : "neutral",
                        )}`}
                      >
                        {row.mode_shape}
                      </span>
                    </td>
                    <td className="px-3 py-2">
                      <TraceableValue
                        onTraceClick={() =>
                          showAlertTrace(
                            t(
                              "ai.decision_registry.trace.thresholds",
                              `Confidence thresholds per autonomy mode (commandment 3): Conservative ${row.confidence_thresholds.conservative} · Standard ${row.confidence_thresholds.standard} · Aggressive ${row.confidence_thresholds.aggressive}. Below threshold escalates to human; above auto-resolves.`,
                            ),
                          )
                        }
                      >
                        <span className="text-xs text-text-secondary">
                          C: {row.confidence_thresholds.conservative} · S:{" "}
                          {row.confidence_thresholds.standard} · A:{" "}
                          {row.confidence_thresholds.aggressive}
                        </span>
                      </TraceableValue>
                    </td>
                    <td className="px-3 py-2 text-xs text-text-secondary">
                      <TraceableValue
                        onTraceClick={() =>
                          showAlertTrace(
                            t(
                              "ai.decision_registry.trace.last_used",
                              row.last_used_at
                                ? `Last invocation at ${row.last_used_at} · ${row.invocation_count} total. Reasoning traces filterable by decision_id.`
                                : "Never invoked — registered but no live calls. Consider auditing.",
                            ),
                          )
                        }
                      >
                        <span dir="ltr">
                          {row.last_used_at?.slice(0, 10) ?? "Never"}
                          {row.invocation_count > 0 && (
                            <span className="ms-1 text-text-tertiary">
                              ({row.invocation_count})
                            </span>
                          )}
                        </span>
                      </TraceableValue>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {!list.isLoading && filtered.length === 0 && (
          <div className="rounded-2xl border border-dashed border-border-subtle p-6 text-center text-sm text-text-tertiary">
            {t(
              "ai.decision_registry.empty",
              "Decision Registry empty — this shouldn't happen. Check agent_runtime/decision_registry.py is being imported at startup.",
            )}
          </div>
        )}
      </div>
      <aside className="rounded-2xl border border-border-subtle bg-white">
        <AiBriefPanel
          agent="mutabaa"
          liveContext={{
            total: stats.total,
            attention: stats.neverUsed,
            critical: stats.humanFirst,
            top_kind: t("ai.decision_registry.title", "Decision Registry"),
            window_label: `${stats.moduleCount} modules`,
          }}
        />
      </aside>
    </div>
  );
}
